/**
 * Inline `(i)` info icon with a hover/focus tooltip.
 *
 * Keyboard-accessible: the button is focusable and the tooltip shows on
 * focus as well as on hover. Plain CSS positioning — no external deps —
 * since we only need short explanations next to metric titles.
 *
 * Usage:
 *   <InfoTooltip text="How this number is computed…" />
 */

import { useId, useState } from "react";

export default function InfoTooltip({
  text,
  className = "",
  iconSize = 14,
  placement = "top",
}) {
  const id = useId();
  const [open, setOpen] = useState(false);

  // Placement classes — we render absolutely-positioned tooltips so
  // they escape the card's overflow context but stay inside the page.
  const placementClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
    right: "left-full top-1/2 -translate-y-1/2 ml-2",
    left: "right-full top-1/2 -translate-y-1/2 mr-2",
  };

  if (!text) return null;

  return (
    <span className={`relative inline-flex ${className}`}>
      <button
        type="button"
        aria-describedby={open ? id : undefined}
        aria-label="More info"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-help"
        style={{ width: iconSize + 4, height: iconSize + 4 }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width={iconSize}
          height={iconSize}
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 2a10 10 0 1 0 .001 20.001A10 10 0 0 0 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
        </svg>
      </button>
      {open && (
        <span
          id={id}
          role="tooltip"
          className={[
            "absolute z-30 w-56 rounded-md bg-slate-900 px-3 py-2 text-xs leading-snug text-white shadow-lg ring-1 ring-black/10 pointer-events-none",
            placementClasses[placement] || placementClasses.top,
          ].join(" ")}
        >
          {text}
        </span>
      )}
    </span>
  );
}
